import { fetchIssue, fetchCurrentUser, fetchActiveSprint, fetchUserByName, createIssue, linkIssues, buildDescriptionAdf, getStoredCreds, storeCreds, clearCreds, getStoredParent, storeParent, JIRA_FIELDS } from './jira-api.js';
import { ddLog, tabId } from './log-store.js';

const FRONTEND_DEVELOPERS = [
  'Anton Zinchenko',
  'Dmytro Siasko',
  'Maksym Ryzhykov',
  'Maksym Yavir',
  'Olena Shchadylo',
  'Olha Slisarchuk',
  'Orest Saturskyy',
  'Rita Aloshkina',
  'Vadym Tsummer',
];

const FRONTEND_DEVELOPERS_SET = new Set(FRONTEND_DEVELOPERS);

const BACKEND_DEVELOPERS = [
  'Anna Mashtalir',
  'Anton Chupak',
  'Denys Khomiuk',
  'Maksym Stankovskyi',
  'Marian Rebeha',
  'Mykhailo Dovhanych',
  'Oleh Chupa',
  'Orest Kosyk',
  'Orest Ostashuk',
  'Rafael Gibim',
  'Serhii Bodnaruk',
  'Serhii Svyrydov',
  'Vasyl Dalekorei',
  'Viacheslav Mozulevskyi',
  'Viktor Sych',
  'Volodymyr Shafran',
  'Yevhenii Moldavchuk',
  'Yura Brehey',
];

const BACKEND_DEVELOPERS_SET = new Set(BACKEND_DEVELOPERS);

const JIRA_BROWSE_BASE = 'https://over-haul.atlassian.net/browse';

function resolveAssigneePromise(devs) {
  return devs[0]?.value ? fetchUserByName(devs[0].value) : Promise.resolve(null);
}

function buildTeamEntry(assignee, devs, baseLabels, stripLabel, addLabel) {
  return {
    assigneeId: assignee?.accountId ?? null,
    assigneeName: assignee?.displayName ?? null,
    assignedTo: devs.map(t => ({ value: t.value })),
    labels: [...new Set([...baseLabels.filter(l => l !== stripLabel), addLabel])],
  };
}

function resolveTeamFields(team, fe, be) {
  if (team === 'fe') return { assigneeId: fe?.assigneeId ?? null, assignedTo: fe?.assignedTo ?? [], labels: fe?.labels ?? [] };
  if (team === 'be') return { assigneeId: be?.assigneeId ?? null, assignedTo: be?.assignedTo ?? [], labels: be?.labels ?? [] };
  return {
    assigneeId: fe?.assigneeId ?? be?.assigneeId ?? null,
    assignedTo: [...(fe?.assignedTo ?? []), ...(be?.assignedTo ?? [])],
    labels: [...new Set([...(fe?.labels ?? []), ...(be?.labels ?? [])])],
  };
}

function buildIssueFields(summary, priority, description, currentUser, parentData) {
  const fields = {
    summary,
    priority: { name: priority },
    [JIRA_FIELDS.DESCRIPTION_BUG]: description,
  };
  if (currentUser) fields.reporter = { accountId: currentUser.accountId };
  if (parentData?.sprintId) fields[JIRA_FIELDS.SPRINT] = parentData.sprintId;
  if (parentData?.epicKey) fields.parent = { key: parentData.epicKey };
  return fields;
}

function ensureNavFirst(entries) {
  if (entries.some(e => e.type === 'nav')) return entries;
  const firstEntry = entries[0];
  const entryTab = firstEntry?.tab ?? tabId;
  let searchLog;
  if (firstEntry === undefined) {
    searchLog = ddLog;
  } else {
    const idx = ddLog.findIndex(e => e.id === firstEntry.id);
    searchLog = idx > 0 ? ddLog.slice(0, idx) : [];
  }
  const lastNav = [...searchLog].reverse().find(e => e.type === 'nav' && e.tab === entryTab);
  const navUrl = lastNav?.url ?? window.location.href;
  const navEntry = lastNav
    ? { ...lastNav, label: `Navigated to "${navUrl}"` }
    : { t: firstEntry?.t ?? new Date().toISOString(), type: 'nav', label: `Navigated to "${navUrl}"`, url: navUrl, tab: entryTab };
  return [navEntry, ...entries];
}

function buildSteps(entries) {
  return entries.map(e => {
    const testIdPart = e.testId ? ` [data-test-id: ${e.testId}]` : '';
    return `${e.label}${testIdPart}`;
  });
}

async function applyParentUpdates(newKey, parentData) {
  if (parentData?.key) await linkIssues(newKey, parentData.key);
}

function teamLabels(team) {
  if (team === 'fe') return ['FRONTEND'];
  if (team === 'be') return ['BACKEND'];
  return ['FRONTEND', 'BACKEND'];
}

const FORM_STYLES = `
  #jira-section {
    display: flex; flex-direction: column; flex-shrink: 0;
    border-left: 2px solid #e5e7eb;
    width: 380px; overflow-y: auto;
    padding: 16px 18px; gap: 14px;
    border-radius: 0 0 12px 0;
    background: #fafafa;
  }
  .jf-field { display: flex; flex-direction: column; gap: 4px; }
  .jf-label { font-size: 11px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: .5px; }
  .jf-input {
    border: 1px solid #d1d5db; border-radius: 6px; padding: 7px 10px;
    font-size: 13px; outline: none; width: 100%; background: #fff;
    font-family: inherit;
  }
  .jf-input:focus { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99,102,241,.15); }
  .jf-hint { font-size: 12px; color: #6b7280; margin-top: 3px; min-height: 16px; }
  .jf-hint.error { color: #dc2626; }
  .jf-hint.loading { color: #6366f1; }
  textarea.jf-input { resize: vertical; min-height: 64px; }
  .jf-row { display: flex; align-items: center; gap: 10px; }
  .jf-submit {
    background: #1e1b4b; color: #fff; border: none; border-radius: 6px;
    padding: 8px 20px; font-size: 13px; font-weight: 600; cursor: pointer; flex-shrink: 0;
  }
  .jf-submit:hover:not(:disabled) { background: #312e81; }
  .jf-submit:disabled { opacity: .45; cursor: not-allowed; }
  .jf-status { font-size: 12px; flex: 1; }
  .jf-status.success { color: #059669; }
  .jf-status.error { color: #dc2626; }
  .jf-two { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
  #jf-creds-screen { display: flex; flex-direction: column; gap: 12px; }
  #jf-creds-screen.hidden { display: none; }
  #jf-main-form { display: flex; flex-direction: column; gap: 14px; }
  #jf-main-form.hidden { display: none; }
  .jf-creds-footer { display: flex; align-items: center; gap: 8px; margin-top: 4px; }
  .jf-link-btn { background: none; border: none; color: #6366f1; font-size: 11px; cursor: pointer; padding: 0; text-decoration: underline; }
  .jf-toggle-group { display: flex; border-radius: 6px; overflow: hidden; border: 1px solid #d1d5db; }
  .jf-toggle { flex: 1; background: #fff; border: none; border-right: 1px solid #d1d5db; padding: 6px 8px; font-size: 12px; font-weight: 500; cursor: pointer; color: #374151; font-family: inherit; }
  .jf-toggle:last-child { border-right: none; }
  .jf-toggle.active { background: #1e1b4b; color: #fff; }
  .jf-toggle:hover:not(.active) { background: #f3f4f6; }
`;

export function initJiraForm({ root, getSelectedEntries, getReplayUrl, clearSelection }) {
  // Inject scoped styles
  const styleEl = document.createElement('style');
  styleEl.textContent = FORM_STYLES;
  root.appendChild(styleEl);

  // Build section HTML
  const section = document.createElement('div');
  section.id = 'jira-section';
  section.innerHTML = `
    <div id="jf-creds-screen">
      <div class="jf-field">
        <span class="jf-label">Jira Credentials</span>
        <input class="jf-input" id="jf-creds-input" type="text" placeholder="email@over-haul.com:YOUR_API_TOKEN" autocomplete="off" spellcheck="false" />
        <div class="jf-hint">Enter <b>email:api_token</b>. Stored locally in this browser only. Get a token at <a href="https://id.atlassian.com/manage-profile/security/api-tokens" target="_blank" rel="noopener" style="color:#6366f1">id.atlassian.com</a>.</div>
      </div>
      <div class="jf-row">
        <button class="jf-submit" id="jf-save-creds">Save &amp; Continue</button>
        <span class="jf-status" id="jf-creds-status"></span>
      </div>
    </div>
    <div id="jf-main-form" class="hidden">
      <div class="jf-field">
        <span class="jf-label">Parent Ticket</span>
        <input class="jf-input" id="jf-parent" type="text" placeholder="OK-1234" autocomplete="off" spellcheck="false" />
        <div class="jf-hint" id="jf-parent-hint"></div>
      </div>
      <div class="jf-field">
        <span class="jf-label">Team</span>
        <div class="jf-toggle-group" id="jf-team-toggle">
          <button class="jf-toggle active" data-value="fe">Frontend</button>
          <button class="jf-toggle" data-value="be">Backend</button>
          <button class="jf-toggle" data-value="both">Both</button>
        </div>
      </div>
      <div class="jf-two">
        <div class="jf-field">
          <span class="jf-label">Summary *</span>
          <input class="jf-input" id="jf-summary" type="text" placeholder="Brief description of the bug" autocomplete="off" />
        </div>
        <div class="jf-field">
          <span class="jf-label">Priority</span>
          <select class="jf-input" id="jf-priority">
            <option>Medium</option>
            <option>High</option>
            <option>Highest</option>
            <option>Low</option>
            <option>Lowest</option>
          </select>
        </div>
      </div>
      <div class="jf-field">
        <span class="jf-label">Actual Result</span>
        <textarea class="jf-input" id="jf-actual" placeholder="What actually happened?" autocomplete="off"></textarea>
      </div>
      <div class="jf-field">
        <span class="jf-label">Expected Result</span>
        <textarea class="jf-input" id="jf-expected" placeholder="What should have happened?" autocomplete="off"></textarea>
      </div>
      <div class="jf-creds-footer">
        <button class="jf-submit" id="jf-submit">Create Bug</button>
        <span class="jf-status" id="jf-status"></span>
        <button class="jf-link-btn" id="jf-change-creds" style="margin-left:auto">Change credentials</button>
      </div>
    </div>
  `;
  root.getElementById('body-row').appendChild(section);

  // Element refs
  const credsScreen = root.getElementById('jf-creds-screen');
  const mainForm = root.getElementById('jf-main-form');
  const parentInput = root.getElementById('jf-parent');
  const parentHint = root.getElementById('jf-parent-hint');
  const submitBtn = root.getElementById('jf-submit');
  const statusEl = root.getElementById('jf-status');

  let parentData = null;
  let currentUser = null;
  let activeSprint = null;
  let selectedTeam = 'fe';

  root.getElementById('jf-team-toggle').querySelectorAll('.jf-toggle').forEach(btn => {
    btn.addEventListener('click', () => {
      selectedTeam = btn.dataset.value;
      root.getElementById('jf-team-toggle').querySelectorAll('.jf-toggle').forEach(b => b.classList.toggle('active', b === btn));
    });
  });

  // Fetch reporter (current user) and active sprint once credentials are confirmed
  function loadCurrentUser() {
    fetchCurrentUser().then(u => { currentUser = u; }).catch(() => { /* ignore */ });
    fetchActiveSprint().then(s => { activeSprint = s; }).catch(() => { /* ignore */ });
  }

  // --- Credentials screen ---
  function showCredsScreen() {
    credsScreen.classList.remove('hidden');
    mainForm.classList.add('hidden');
  }
  function showMainForm() {
    credsScreen.classList.add('hidden');
    mainForm.classList.remove('hidden');
  }

  // Show appropriate screen based on stored creds
  if (getStoredCreds()) {
    showMainForm();
    loadCurrentUser();
    const storedParent = getStoredParent();
    if (storedParent) {
      parentInput.value = storedParent;
      loadParent(storedParent);
    }
  } else showCredsScreen();

  root.getElementById('jf-save-creds').addEventListener('click', () => {
    const val = root.getElementById('jf-creds-input').value.trim();
    const credsStatus = root.getElementById('jf-creds-status');
    if (!val.includes(':')) {
      credsStatus.textContent = 'Format must be email:token';
      credsStatus.className = 'jf-status error';
      return;
    }
    storeCreds(val);
    root.getElementById('jf-creds-input').value = '';
    credsStatus.textContent = '';
    showMainForm();
    loadCurrentUser();
  });

  root.getElementById('jf-change-creds').addEventListener('click', () => {
    clearCreds();
    showCredsScreen();
  });
  // --- Preview refresh (called by ui.js when selection changes) ---
  function refreshPreview() { /* no-op: selection preview removed, kept for ui.js compatibility */ }

  // --- Parent ticket fetch ---
  function clearParent() {
    parentData = null;
    parentHint.textContent = '';
    parentHint.className = 'jf-hint';
    storeParent('');
  }

  async function loadParent(rawKey) {
    const key = rawKey.trim().toUpperCase();
    if (!key) { clearParent(); return; }
    parentHint.textContent = 'Fetching…';
    parentHint.className = 'jf-hint loading';
    parentData = null;
    try {
      const issue = await fetchIssue(key);
      const sprints = issue.fields[JIRA_FIELDS.SPRINT];
      const sprint = Array.isArray(sprints)
        ? (sprints.find(s => s.state === 'active') ?? sprints.at(-1))
        : null;
      const rawLabels = Array.isArray(issue.fields.labels) ? issue.fields.labels : [];
      const rawAssignedTo = Array.isArray(issue.fields[JIRA_FIELDS.ASSIGNED_TO]) ? issue.fields[JIRA_FIELDS.ASSIGNED_TO] : [];
      const filteredFeDevs = rawAssignedTo.filter(t => FRONTEND_DEVELOPERS_SET.has(t.value));
      const filteredBeDevs = rawAssignedTo.filter(t => BACKEND_DEVELOPERS_SET.has(t.value));
      const [feAssignee, beAssignee] = await Promise.all([
        resolveAssigneePromise(filteredFeDevs),
        resolveAssigneePromise(filteredBeDevs),
      ]);
      const baseLabels = rawLabels.filter(l => l !== 'Testing');
      parentData = {
        key: issue.key,
        sprintId: sprint?.id ?? null,
        sprintName: sprint?.name ?? null,
        epicKey: issue.fields.parent?.key ?? null,
        teamOptions: Array.isArray(issue.fields[JIRA_FIELDS.TEAM])
          ? issue.fields[JIRA_FIELDS.TEAM].map(t => ({ value: t.value }))
          : null,
        fe: buildTeamEntry(feAssignee, filteredFeDevs, baseLabels, 'BACKEND', 'FRONTEND'),
        be: buildTeamEntry(beAssignee, filteredBeDevs, baseLabels, 'FRONTEND', 'BACKEND'),
      };
      storeParent(issue.key);
      const feLabel = parentData.fe.assigneeName ? `FE: ${parentData.fe.assigneeName}` : null;
      const beLabel = parentData.be.assigneeName ? `BE: ${parentData.be.assigneeName}` : null;
      const meta = [parentData.sprintName, feLabel, beLabel].filter(Boolean).join(' · ');
      const metaSuffix = meta ? ` — ${meta}` : '';
      parentHint.textContent = `${issue.key}: "${issue.fields.summary}"${metaSuffix}`;
      parentHint.className = 'jf-hint';
    } catch (err) {
      parentHint.textContent = err.message;
      parentHint.className = 'jf-hint error';
    }
  }

  parentInput.addEventListener('blur', () => loadParent(parentInput.value));
  parentInput.addEventListener('keydown', ev => { if (ev.key === 'Enter') loadParent(parentInput.value); });
  parentInput.addEventListener('input', () => { if (!parentInput.value.trim()) clearParent(); });

  // --- Submit ---
  submitBtn.addEventListener('click', async () => {
    const summary = root.getElementById('jf-summary').value.trim();
    if (!summary) {
      statusEl.textContent = 'Summary is required.';
      statusEl.className = 'jf-status error';
      return;
    }

    submitBtn.disabled = true;
    statusEl.textContent = 'Creating…';
    statusEl.className = 'jf-status';

    try {
      const entries = getSelectedEntries();
      const stepsEntries = ensureNavFirst(entries);
      const replayUrl = getReplayUrl(stepsEntries[0] ?? null);
      const actualResult = root.getElementById('jf-actual').value.trim();
      const expectedResult = root.getElementById('jf-expected').value.trim();
      const priority = root.getElementById('jf-priority').value;
      const steps = buildSteps(stepsEntries);
      const description = buildDescriptionAdf(steps, replayUrl, { actualResult, expectedResult });
      const fields = buildIssueFields(summary, priority, description, currentUser, parentData);
      if (!parentData && activeSprint) fields[JIRA_FIELDS.SPRINT] = activeSprint.id;
      const { assigneeId, assignedTo, labels } = resolveTeamFields(selectedTeam, parentData?.fe, parentData?.be);
      if (assigneeId) fields.assignee = { accountId: assigneeId };
      const effectiveLabels = labels.length > 0 ? labels : teamLabels(selectedTeam);
      fields.labels = [...new Set([...effectiveLabels, 'DEVELOPMENT'])];
      if (parentData?.teamOptions?.length) fields[JIRA_FIELDS.TEAM] = parentData.teamOptions;
      if (assignedTo.length) fields[JIRA_FIELDS.ASSIGNED_TO] = assignedTo;

      const { key: newKey } = await createIssue(fields);

      await applyParentUpdates(newKey, parentData);

      const ticketUrl = `${JIRA_BROWSE_BASE}/${newKey}`;
      statusEl.textContent = '\u2713 Created ';
      const ticketLink = document.createElement('a');
      ticketLink.href = ticketUrl;
      ticketLink.target = '_blank';
      ticketLink.rel = 'noopener';
      ticketLink.style.cssText = 'color:#059669;font-weight:600';
      ticketLink.textContent = newKey;
      statusEl.appendChild(ticketLink);
      statusEl.className = 'jf-status success';

      root.getElementById('jf-summary').value = '';
      root.getElementById('jf-actual').value = '';
      root.getElementById('jf-expected').value = '';
      clearSelection();
    } catch (err) {
      statusEl.textContent = err.message;
      statusEl.className = 'jf-status error';
    } finally {
      submitBtn.disabled = false;
    }
  });

  return { refreshPreview, getParentKey: () => parentInput.value };
}
